import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  AccountBalance as CashIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import GirviPageShell from '../common/GirviPageShell';
import baseURL from '../../baseURL';

const IllegalCashBalance = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [cashBalance, setCashBalance] = useState(null);
  
  // Form states for Illegal
  const [illegalOpeningBalanceForm, setIllegalOpeningBalanceForm] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    financialYear: ''
  });
  
  const [illegalAddCashForm, setIllegalAddCashForm] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: 'Cash added to illegal cash in hand'
  });
  
  const [illegalDeductCashForm, setIllegalDeductCashForm] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: 'Cash deducted from illegal cash in hand'
  });

  // Fetch cash balance on component mount
  useEffect(() => {
    fetchCashBalance();
  }, []);

  const fetchCashBalance = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/cash-balance`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch cash balance');
      }

      const data = await response.json();
      setCashBalance(data.data);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error loading cash balance',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSetOpeningBalance = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/cash-balance/illegal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(illegalOpeningBalanceForm)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to set opening balance');
      }

      setSnackbar({
        open: true,
        message: data.message || 'Opening balance set successfully',
        severity: 'success'
      });

      setIllegalOpeningBalanceForm({
        amount: '',
        date: new Date().toISOString().split('T')[0],
        financialYear: ''
      });

      fetchCashBalance();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Error setting opening balance',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddCash = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/cash-balance/illegal/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(illegalAddCashForm)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to add cash');
      }

      setSnackbar({
        open: true,
        message: data.message || 'Cash added successfully',
        severity: 'success'
      });

      setIllegalAddCashForm({
        amount: '',
        date: new Date().toISOString().split('T')[0],
        description: 'Cash added to illegal cash in hand'
      });

      fetchCashBalance();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Error adding cash',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeductCash = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/cash-balance/illegal/deduct`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(illegalDeductCashForm)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to deduct cash');
      }

      setSnackbar({
        open: true,
        message: data.message || 'Cash deducted successfully',
        severity: 'success'
      });

      setIllegalDeductCashForm({
        amount: '',
        date: new Date().toISOString().split('T')[0],
        description: 'Cash deducted from illegal cash in hand'
      });

      fetchCashBalance();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Error deducting cash',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <GirviPageShell
        title="Cash in Hand"
        subtitle="Black · Cash"
        chipLabel="BLACK"
        maxWidth="xl"
        compact
      >
        {/* Current Balance Card */}
        <Card sx={{ mb: 3, bgcolor: 'primary.main', color: '#f5e6c8' }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Current Illegal Cash Balance
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  ₹{loading ? '...' : (cashBalance?.illegal?.balance?.toFixed(2) || '0.00')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} sx={{ textAlign: { md: 'right' } }}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={fetchCashBalance}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <CashIcon />}
                >
                  Refresh Balance
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab icon={<SettingsIcon />} label="Opening Balance" />
            <Tab icon={<AddIcon />} label="Add Cash" />
            <Tab icon={<RemoveIcon />} label="Deduct Cash" />
          </Tabs>
        </Paper>

        {/* Tab Content */}
        <Paper sx={{ p: 3 }}>
          {/* Opening Balance Tab */}
          {activeTab === 0 && (
            <Box component="form" onSubmit={handleSetOpeningBalance}>
              <Typography variant="h6" gutterBottom>
                Set Opening Cash Balance - Illegal Account
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Set the initial cash balance for the financial year. This entry will not appear in daybook or journal.
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Amount"
                    type="number"
                    required
                    value={illegalOpeningBalanceForm.amount}
                    onChange={(e) => setIllegalOpeningBalanceForm({ ...illegalOpeningBalanceForm, amount: e.target.value })}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Date"
                    type="date"
                    required
                    value={illegalOpeningBalanceForm.date}
                    onChange={(e) => setIllegalOpeningBalanceForm({ ...illegalOpeningBalanceForm, date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Financial Year (Optional)"
                    value={illegalOpeningBalanceForm.financialYear}
                    onChange={(e) => setIllegalOpeningBalanceForm({ ...illegalOpeningBalanceForm, financialYear: e.target.value })}
                    placeholder="e.g., 2024-25"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={20} /> : <SettingsIcon />}
                  >
                    {submitting ? 'Setting...' : 'Set Opening Balance'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Add Cash Tab */}
          {activeTab === 1 && (
            <Box component="form" onSubmit={handleAddCash}>
              <Typography variant="h6" gutterBottom>
                Add Cash to Illegal Cash in Hand
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Add cash to your illegal cash in hand. This entry will not appear in daybook or journal.
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Amount"
                    type="number"
                    required
                    value={illegalAddCashForm.amount}
                    onChange={(e) => setIllegalAddCashForm({ ...illegalAddCashForm, amount: e.target.value })}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Date"
                    type="date"
                    required
                    value={illegalAddCashForm.date}
                    onChange={(e) => setIllegalAddCashForm({ ...illegalAddCashForm, date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={illegalAddCashForm.description}
                    onChange={(e) => setIllegalAddCashForm({ ...illegalAddCashForm, description: e.target.value })}
                    placeholder="e.g., Cash deposit from bank"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="success"
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={20} /> : <AddIcon />}
                  >
                    {submitting ? 'Adding...' : 'Add Cash'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Deduct Cash Tab */}
          {activeTab === 2 && (
            <Box component="form" onSubmit={handleDeductCash}>
              <Typography variant="h6" gutterBottom>
                Deduct Cash from Illegal Cash in Hand
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Deduct cash from your illegal cash in hand. This entry will not appear in daybook or journal.
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Amount"
                    type="number"
                    required
                    value={illegalDeductCashForm.amount}
                    onChange={(e) => setIllegalDeductCashForm({ ...illegalDeductCashForm, amount: e.target.value })}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Date"
                    type="date"
                    required
                    value={illegalDeductCashForm.date}
                    onChange={(e) => setIllegalDeductCashForm({ ...illegalDeductCashForm, date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={illegalDeductCashForm.description}
                    onChange={(e) => setIllegalDeductCashForm({ ...illegalDeductCashForm, description: e.target.value })}
                    placeholder="e.g., Cash withdrawal for expenses"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="error"
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={20} /> : <RemoveIcon />}
                  >
                    {submitting ? 'Deducting...' : 'Deduct Cash'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </GirviPageShell>
    </>
  );
};

export default IllegalCashBalance;
